/**
 * Map click and hover interaction handlers.
 *
 * Architecture: MapboxOverlay handles pointer-events coordination between
 * deck.gl and MapLibre automatically. We wire onClick/onHover callbacks on the
 * overlay for picking, and use a separate MapLibre mousemove for country hover
 * detection (throttled to avoid expensive GeoJSON ray-casting on every frame).
 */

import type { PickingInfo } from '@deck.gl/core';
import type maplibregl from 'maplibre-gl';
import type { NormalizedEvent } from '@/sources/base-source';
import type { ClusterResult } from '@/map/clustering';
import { eventBus } from '@/core/events';
import { getMap } from '@/map/engine';
import { getOverlay } from '@/map/globe';
import { showTooltip, hideTooltip } from '@/map/tooltip';
import { detectCountry, initCountryDetection } from '@/map/country-detect';

/** Throttle helper — returns a throttled version of a function. */
function throttle<T extends (...args: unknown[]) => void>(fn: T, ms: number): T {
  let lastCall = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  return ((...args: unknown[]) => {
    const now = Date.now();
    const remaining = ms - (now - lastCall);

    if (remaining <= 0) {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      lastCall = now;
      fn(...args);
    } else if (timer === null) {
      timer = setTimeout(() => {
        lastCall = Date.now();
        timer = null;
        fn(...args);
      }, remaining);
    }
  }) as T;
}

/** Check if a picked object is a cluster feature. */
function isCluster(obj: unknown): obj is ClusterResult {
  const record = obj as Record<string, unknown> | undefined;
  return !!record && record.isCluster === true && typeof record.clusterId === 'number';
}

/** Check if a picked object is a NormalizedEvent. */
function isEvent(obj: unknown): obj is NormalizedEvent {
  const record = obj as Record<string, unknown> | undefined;
  return !!record && typeof record.id === 'string' && typeof record.title === 'string';
}

/** Check if a picked object is a static data point (base, nuclear, port, etc.). */
function isStaticMarker(obj: unknown): obj is { id: string; name: string; [key: string]: unknown } {
  const record = obj as Record<string, unknown> | undefined;
  return !!record && typeof record.id === 'string' && typeof record.name === 'string' && !('title' in record);
}

/**
 * Convert a static data object to a minimal NormalizedEvent for tooltip display.
 */
function staticToEvent(obj: { id: string; name: string; [key: string]: unknown }): NormalizedEvent {
  return {
    id: obj.id,
    title: obj.name,
    source: (obj.operator as string) ?? (obj.country as string) ?? 'static',
    timestamp: new Date().toISOString(),
    type: ((obj.type as string) ?? 'infrastructure') as NormalizedEvent['type'],
    severity: 'info',
    description: (obj.description as string) ?? undefined,
    location: typeof obj.lat === 'number' && typeof obj.lon === 'number'
      ? { lat: obj.lat as number, lng: obj.lon as number }
      : typeof obj.lat === 'number' && typeof obj.lng === 'number'
        ? { lat: obj.lat as number, lng: obj.lng as number }
        : undefined,
  };
}

/**
 * Handle deck.gl hover pick.
 * Shows tooltip for events and static markers, hides otherwise. Updates cursor.
 */
function handleOverlayHover(info: PickingInfo): void {
  const obj = info.object;
  const map = getMap();

  if (obj && isEvent(obj)) {
    showTooltip(info.x, info.y, obj);
    if (map) map.getCanvas().style.cursor = 'pointer';
  } else if (obj && isStaticMarker(obj)) {
    showTooltip(info.x, info.y, staticToEvent(obj));
    if (map) map.getCanvas().style.cursor = 'pointer';
  } else if (obj && isCluster(obj)) {
    hideTooltip();
    if (map) map.getCanvas().style.cursor = 'pointer';
  } else {
    hideTooltip();
    if (map) map.getCanvas().style.cursor = '';
  }
}

/**
 * Handle deck.gl click pick.
 * Cluster → zoom in. Event → select + fly. Background → country detect.
 */
function handleOverlayClick(info: PickingInfo): void {
  const obj = info.object;

  // Cluster click — zoom into the cluster
  if (isCluster(obj)) {
    const map = getMap();
    if (map) {
      map.flyTo({
        center: [obj.lng, obj.lat],
        zoom: map.getZoom() + 2,
        duration: 500,
      });
    }
    return;
  }

  // Event marker click — select and fly to location
  if (isEvent(obj)) {
    eventBus.emit('event:selected', obj);

    if (obj.location) {
      const map = getMap();
      if (map) {
        map.flyTo({
          center: [obj.location.lng, obj.location.lat],
          zoom: Math.max(map.getZoom(), 6),
          duration: 600,
        });
      }
    }
    return;
  }

  // Static marker click — convert to event format and select
  if (isStaticMarker(obj)) {
    const syntheticEvent = staticToEvent(obj);
    eventBus.emit('event:selected', syntheticEvent);

    if (syntheticEvent.location) {
      const map = getMap();
      if (map) {
        map.flyTo({
          center: [syntheticEvent.location.lng, syntheticEvent.location.lat],
          zoom: Math.max(map.getZoom(), 8),
          duration: 600,
        });
      }
    }
    return;
  }

  // Background click — run country detection on click coordinates
  if (info.coordinate) {
    const [lng, lat] = info.coordinate;
    const country = detectCountry(lat, lng);
    if (country) {
      eventBus.emit('country:selected', country);
    }
  }
}

/** Throttled country hover detection on MapLibre mousemove. */
const handleCountryHover = throttle(((e: maplibregl.MapMouseEvent) => {
  const country = detectCountry(e.lngLat.lat, e.lngLat.lng);
  eventBus.emit('country:hover', country);
}) as (...args: unknown[]) => void, 200);

let cleanupFns: (() => void)[] = [];

/**
 * Initialize all map interaction handlers.
 * Wires deck.gl overlay callbacks for pick events and MapLibre mousemove
 * for country hover detection.
 */
export function initMapInteractions(): void {
  destroyMapInteractions();

  // Fire-and-forget — country detection loads async, detectCountry() returns null until ready
  initCountryDetection().catch((err) => {
    console.warn('[Sentinel] Country detection init failed:', err);
  });

  const overlay = getOverlay();
  const map = getMap();

  // Wire deck.gl picking callbacks on the overlay
  if (overlay) {
    overlay.setProps({
      onHover: handleOverlayHover,
      onClick: handleOverlayClick,
    });

    cleanupFns.push(() => {
      overlay.setProps({ onHover: undefined, onClick: undefined });
    });
  }

  // Wire MapLibre mousemove for country hover detection (throttled)
  if (map) {
    map.on('mousemove', handleCountryHover);
    cleanupFns.push(() => {
      map.off('mousemove', handleCountryHover);
    });
  }
}

/** Clean up all interaction handlers. */
export function destroyMapInteractions(): void {
  for (const fn of cleanupFns) {
    fn();
  }
  cleanupFns = [];
}
